/**
 * Log Payment Validation Schema - Sử dụng Yup để định nghĩa validation rules
 * Đồng bộ với backend Laravel/NestJS
 */

import * as yup from "yup";
import {
  PaymentMethodEnum,
  PaymentMethodLabel,
  PaymentMethodCodeByLabel,
  CardPurposeEnum,
  RecipientAccountLabel,
  FeePaymentMethodLabel,
} from "@/core/enums/logPayment.enum";

/**
 * Schema validation cho từng field của log payment
 * @type {Object}
 */
export const logPaymentSchema = {
  // Required fields - theo rules từ hình ảnh
  customer_phone: yup
    .string()
    .required("Thiếu số điện thoại")
    .matches(/^0[3-9][0-9]{8}$/, "Số điện thoại không đúng định dạng"),

  contract_id: yup
    .number()
    .typeError("Định dạng mã hợp đồng không hợp lệ")
    .required("Thiếu mã hợp đồng")
    .min(1, "Mã hợp đồng phải là số nguyên dương"),

  amount: yup
    .number()
    .typeError("Định dạng số tiền nạp không hợp lệ")
    .required("Thiếu số tiền nạp")
    .min(1, "Số tiền nạp phải lớn hơn 0"),

  payment_method: yup
    .string()
    .required("Thiếu hình thức thanh toán")
    .oneOf(
      ["Chuyển khoản", "Quẹt thẻ", "Tiền mặt"],
      "Hình thức thanh toán phải là 'Chuyển khoản', 'Quẹt thẻ' hoặc 'Tiền mặt'"
    ),

  // Conditional required fields - theo rules từ hình ảnh
  recipient_account: yup
    .number()
    .typeError("Định dạng tài khoản nhận tiền không hợp lệ")
    .nullable()
    .oneOf([1, 2], "Tài khoản nhận tiền phải là 1 (Cá nhân) hoặc 2 (Công ty)"),

  card_purpose: yup
    .number()
    .typeError("Định dạng mục đích quẹt thẻ không hợp lệ")
    .nullable()
    .oneOf([0, 1], "Mục đích quẹt thẻ phải là 0 (Trả thẳng) hoặc 1 (Trả góp)"),

  fee_payment_method: yup
    .number()
    .typeError("Định dạng hình thức thanh toán phí không hợp lệ")
    .nullable()
    .oneOf([1, 2, 3, 4], "Hình thức thanh toán phí phải là 1, 2, 3 hoặc 4"),
};

/**
 * Helper function để lấy payment method code từ value
 * @param {*} value - Giá trị payment method
 * @returns {Number|undefined}
 */
function getPaymentMethodCode(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string") return PaymentMethodCodeByLabel[value.trim()];
  return undefined;
}

/**
 * Validate một field với schema tương ứng
 * @param {String} field - Tên field
 * @param {*} value - Giá trị cần validate
 * @returns {Promise<Object>} Kết quả validation
 */
export async function validateLogPaymentField(field, value) {
  if (!logPaymentSchema[field]) {
    return { isValid: true };
  }

  try {
    await logPaymentSchema[field].validate(value);
    return { isValid: true };
  } catch (error) {
    return {
      isValid: false,
      message: error.message,
    };
  }
}

/**
 * Validate toàn bộ object với schema
 * @param {Object} data - Object cần validate
 * @returns {Promise<Object>} Kết quả validation
 */
export async function validateLogPaymentObject(data) {
  const errors = {};

  for (const field in logPaymentSchema) {
    const result = await validateLogPaymentField(field, data[field]);
    if (!result.isValid) {
      errors[field] = result.message;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
